import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../../common/constants';
import envConfig from '../../../common/env';
import utils from '../../../common/utils';

export default class GetTenantConfigService extends BaseService {
  constructor() {
    super(constants.GET_TENANT_CONFIG_DATA, 'getTenantConfigStatus');
  }

  async handleEvent() {
    const { isStackB, env } = utils.getStackBParams();
    const currentEnv = envConfig.ENVIRONMENT;
    const shouldCallStackB =
      isStackB && (env === constants.ENV_PROD || (env === constants.ENV_STG && currentEnv !== constants.ENV_STG));

    if (shouldCallStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders({});
      const [currentResponse, stackBResponse] = await Promise.allSettled([
        this.getClient('aiadminv2').get('/tenants'),
        this.getClient('aiadminstackbv2').get('/tenants', { headers: enhancedHeaders })
      ]);

      // If current env call itself failed, fall through to handleError
      if (currentResponse.status === 'rejected') {
        throw currentResponse.reason;
      }

      const currentTenants = currentResponse.value?.data?.data || [];

      // If Stack B call failed, show notification but still return current tenants
      if (stackBResponse.status === 'rejected') {
        const notification = {
          open: true,
          message: 'Error fetching Stack B tenants. Showing current environment tenants.',
          type: 'error'
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
        return { data: { data: currentTenants } };
      }

      const stackBTenants = stackBResponse.value?.data?.data || [];

      const stackBTenantsByName = new Map(stackBTenants.map((st) => [st.name, st]));
      const currentTenantNames = new Set(currentTenants.map((ct) => ct.name));

      // Tenants in both envs: use Stack B's tenantId + current env's config
      const mergedTenants = currentTenants
        .filter((ct) => stackBTenantsByName.has(ct.name))
        .map((ct) => ({
          ...ct,
          tenantId: stackBTenantsByName.get(ct.name).tenantId
        }));

      // Tenants only in Stack B: add entirely with Stack B's config
      const stackBOnlyTenants = stackBTenants.filter((st) => !currentTenantNames.has(st.name));

      return { data: { data: [...mergedTenants, ...stackBOnlyTenants] } };
    }

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders({});
      try {
        return await this.getClient('aiadminstackbv2').get('/tenants', { headers: enhancedHeaders });
      } catch (err) {
        const notification = {
          open: true,
          message: 'Error fetching Stack B tenants. Showing current environment tenants.',
          type: 'error'
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
        return this.getClient('aiadminv2').get('/tenants');
      }
    }

    return this.getClient('aiadminv2').get('/tenants');
  }

  handleResponse(response) {
    Framework.getStoreRegistry().getStore('tenantConfig').setResponse(response.data);
  }

  handleError(error) {
    const { response } = error;
    if (response) {
      const { status } = response;
      if (status === 401) {
        utils.redirectToLogin();
      }
    }
    const notification = {
      open: true,
      message: 'Error fetching tenant config',
      type: 'error'
    };
    Framework.getStoreRegistry().getStore('notificationState').set(notification);
  }
}
